'use client';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/lib/ui-kit/icons';

const SHOW_AT_SCROLL_PX_COMPACT = 400;
const SHOW_AT_SCROLL_PX_DESKTOP = 1500;
const PINNED_OFFSET_PX = 24;
// Below 1024px the BottomNavBar (~64px tall) is fixed at the bottom, so push
// the button above it with a small gap.
const COMPACT_OFFSET_PX = 90;
const COMPACT_BREAKPOINT = 1023;

// Hover state lives in framer variants, not React state — re-rendering on hover
// would restart the entry animation and make the button jump under the cursor.
const buttonVariants = {
  rest: { scale: 1 },
  hover: { scale: 1.06 },
};

// Lottie-like one-shot: two stacked arrows scroll upwards behind a mask, so the
// top one leaves through the top while the next one rises into its place. The
// arrows are identical, so the end state (-50%) is pixel-identical to the start
// state (0%) — the reset back to `rest` is instant and therefore invisible,
// which is what keeps this a single play rather than a rewind.
const arrowVariants = {
  rest: {
    y: '0%',
    transition: { duration: 0 },
  },
  hover: {
    y: '-50%',
    transition: {
      duration: 0.5,
      ease: [0.65, 0, 0.35, 1] as [number, number, number, number],
    },
  },
};

export function BackToTopButton() {
  const t = useTranslations('common');
  const reduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const isCompactRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${COMPACT_BREAKPOINT}px)`);
    const update = () => {
      setIsCompact(mq.matches);
      isCompactRef.current = mq.matches;
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const compute = () => {
      const showAt = isCompactRef.current ? SHOW_AT_SCROLL_PX_COMPACT : SHOW_AT_SCROLL_PX_DESKTOP;
      setVisible(window.scrollY > showAt);
    };
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute, { passive: true });
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, []);

  // Centred, so it never shares the right corner with the chat bubble or the daily card.
  const bottomOffset = isCompact ? COMPACT_OFFSET_PX : PINNED_OFFSET_PX;

  const handleClick = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    // The animated child stays button-sized so `scale` pivots on the button,
    // not on the whole strip.
    <div
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center"
      style={{ bottom: `${bottomOffset}px` }}
    >
      <AnimatePresence>
        {visible && (
          <motion.div
            key="back-to-top"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 28, scale: 0.55 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: reduceMotion ? 0 : 8,
              scale: reduceMotion ? 1 : 0.8,
              transition: { duration: 0.12, ease: 'easeIn' },
            }}
            transition={
              reduceMotion
                ? { duration: 0.15 }
                : {
                    // Spring overshoot is the jump — no hand-rolled keyframes, so
                    // a re-render can't restart it mid-flight.
                    y: { type: 'spring', stiffness: 620, damping: 14, mass: 0.7 },
                    scale: { type: 'spring', stiffness: 620, damping: 13, mass: 0.7 },
                    opacity: { duration: 0.15, ease: 'easeOut' },
                  }
            }
            className="transform-gpu will-change-transform"
          >
            <motion.button
              type="button"
              onClick={handleClick}
              variants={buttonVariants}
              initial="rest"
              animate="rest"
              whileHover={reduceMotion ? undefined : 'hover'}
              whileFocus={reduceMotion ? undefined : 'hover'}
              whileTap={reduceMotion ? undefined : { scale: 0.94 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="pointer-events-auto flex cursor-pointer items-center justify-center gap-2 rounded-[10px] bg-v2-btn-secondary px-4 py-3 font-medium text-[13px] text-v2-btn-secondary-text leading-4 tracking-[-0.01em] transition-colors duration-150 hover:bg-v2-btn-secondary-hover"
            >
              {t('backToTop')}
              <span className="relative block size-5 overflow-hidden">
                <motion.span
                  variants={arrowVariants}
                  className="absolute inset-x-0 top-0 flex flex-col"
                >
                  <Icon name="ArrowUp2" size={20} />
                  <Icon name="ArrowUp2" size={20} />
                </motion.span>
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
